import _ from 'lodash';
import { RefObject, useEffect, useMemo, useRef } from 'react';
import * as Constants from 'const';
import { getContainerSize } from 'utils/getContainerSize';
import { CTAStyles, CTAStylesSetters } from '../utils/styles';

export function useResizeObserverCTA(
  cellContainerRef: RefObject<HTMLDivElement>,
  editorWrapperRef: RefObject<HTMLDivElement>,
  styles: CTAStyles,
  stylesSetters: CTAStylesSetters,
  editMode: boolean, // editorWrapperRef will be updated during editMode toggling so we need to resubscribe ResizeObserver
  isDraggingAsset: boolean,
): void {

  const stylesRef = useRef(styles);
  stylesRef.current = styles;

  const stylesSettersRef = useRef(stylesSetters);
  stylesSettersRef.current = stylesSetters;

  const isDraggingAssetRef = useRef(isDraggingAsset);
  isDraggingAssetRef.current = isDraggingAsset;

  const handler = useMemo(
    () => _.debounce<ResizeObserverCallback>(
      (entries) => {
        const currentStyles = stylesRef.current;
        const currentSetters = stylesSettersRef.current;

        if (_.size(entries) === 0 || !currentStyles.assetPadding) {
          return;
        }
        const editorContentHeight = entries[0].target.scrollHeight;
        const wrapperEditorHeight = currentStyles.height
          - currentStyles.assetPadding.get(Constants.BoxPropertySide.TOP)
          - currentStyles.assetPadding.get(Constants.BoxPropertySide.BOTTOM);
        const cellContainerAvailableHeight = cellContainerRef.current
          ? getContainerSize(cellContainerRef.current).height
          : wrapperEditorHeight;
        const extraHeight = editorContentHeight - wrapperEditorHeight;
        if (extraHeight > 0) {
          currentSetters.height(currentStyles.height + extraHeight);
        }

        const isCellSmallerThanWrapper = (cellContainerAvailableHeight > 0) && (cellContainerAvailableHeight < wrapperEditorHeight);
        const isCellLargerThanContent = cellContainerAvailableHeight > editorContentHeight;
        if (isCellSmallerThanWrapper && isCellLargerThanContent && !isDraggingAssetRef.current) {
          currentSetters.height(cellContainerAvailableHeight);
        }
      },
      100,
    ),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const { current } = editorWrapperRef;

    if (!current) {
      return;
    }

    const resizeObserver = new ResizeObserver(handler);
    resizeObserver.observe(current);

    return (): void => {
      handler.cancel();
      resizeObserver.unobserve(current);
    };
  }, [editMode, handler, editorWrapperRef]);
}
